import * as url from "../../helpers/url_helper";
import { fetchApi, processDeleteResponse, apiCall } from "./apiService";
import { handleApiError } from "./helpers";

/**
 * Loads all data needed for reference stores (stores, parent stores, and reference stores)
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Object with stores, parent_store, and referenceStores or null if error
 */
export const loadReferenceStoreData = async (notifyError) => {
    try {
        const [storesResponse, parentStoreResponse, scannerStoreResponse] = await Promise.all([
            fetchApi(url.STORES, 'GET'),
            fetchApi(url.PARENT_STORES, 'GET'),
            fetchApi(url.REFERENCE_STORE, 'GET')
        ]);

        const stores = storesResponse.results;
        const parentStore = parentStoreResponse.results;
        const scannerStore = scannerStoreResponse.results;

        // Check if all responses are successful
        if (storesResponse.response.ok && parentStoreResponse.response.ok && scannerStoreResponse.response.ok) {
            return {
                stores: stores.data || stores,
                parent_store: parentStore.data || parentStore,
                referenceStores: scannerStore.data || scannerStore
            };
        } else {
            // Handle errors for reference stores specifically
            if (!scannerStoreResponse.response.ok) {
                handleApiError(scannerStoreResponse.response, scannerStore, notifyError);
            }
            return null;
        }
    } catch (err) {
        notifyError("Failed to fetch data. Please check your network connection.");
        return null;
    }
};

/**
 * Creates a new reference store
 * @param {Object} storeData - Reference store data (p_store, c_store, is_active)
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Created reference store data or null if error
 */
export const createReferenceStore = async (storeData, notifyError) => {
    // Validate before API call
    if (!storeData.p_store || !storeData.c_store) {
        notifyError("Please select Parent Store and Child Store");
        return null;
    }

    if (storeData.p_store === storeData.c_store) {
        notifyError("Parent Store and Child Store cannot be the same");
        return null;
    }

    return await apiCall(
        url.REFERENCE_STORE,
        'POST',
        storeData,
        notifyError,
        "Reference store creation failed"
    );
};

/**
 * Updates an existing reference store
 * @param {Object} storeData - Reference store data with id
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<Object|null>} Updated reference store data or null if error
 */
export const updateReferenceStore = async (storeData, notifyError) => {
    return await apiCall(
        url.REFERENCE_STORE,
        'PUT',
        storeData,
        notifyError,
        "Reference Store update failed"
    );
};

/**
 * Deletes a reference store
 * @param {string} id - Reference store ID
 * @param {Function} notifySuccess - Success notification function
 * @param {Function} notifyError - Error notification function
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const deleteReferenceStore = async (id, notifySuccess, notifyError) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this reference store?");
    if (!confirmDelete) {
        return false;
    }

    try {
        const { response, results } = await fetchApi(url.REFERENCE_STORE, 'DELETE', { id });

        return processDeleteResponse(
            response,
            results,
            notifySuccess,
            notifyError,
            "Reference store has deleted successfully",
            "Reference store deletion failed"
        );
    } catch (err) {
        notifyError("Reference store has deleted failed, Please check your network connection.");
        return false;
    }
};

